import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { fetchAuthenticatedBlob } from '../api/client';
import { useAuth } from '../features/auth/AuthProvider';

import styles from './UserAvatar.module.scss';

type UserAvatarProps = {
  readonly user: {
    readonly full_name?: string | null;
    readonly avatar_url?: string | null;
  };
  readonly size?: number;
  readonly className?: string;
};

const DEFAULT_SIZE = 28;

function computeInitials(fullName?: string | null): string {
  if (!fullName) {
    return 'WF';
  }
  const initials = fullName
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || 'WF';
}

export function UserAvatar({ user, size = DEFAULT_SIZE, className }: UserAvatarProps) {
  const { tokens } = useAuth();
  const fontSize = Math.max(10, Math.round(size * 0.38));
  const style = {
    '--avatar-size': `${size}px`,
    '--avatar-font-size': `${fontSize}px`,
  } as CSSProperties;

  const wrapperClass = [styles.avatar, className ?? ''].filter(Boolean).join(' ');

  const absoluteHttp = useMemo(() => {
    const trimmed = user.avatar_url?.trim() ?? '';
    if (!trimmed) {
      return null;
    }
    return /^https?:\/\//i.test(trimmed) ? trimmed : null;
  }, [user.avatar_url]);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (absoluteHttp) {
      setBlobUrl(null);
      return;
    }
    const trimmed = user.avatar_url?.trim() ?? '';
    if (!trimmed) {
      setBlobUrl(null);
      return;
    }
    const token = tokens?.access_token;
    if (!token) {
      setBlobUrl(null);
      return;
    }
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchAuthenticatedBlob(path, token).then(
      (blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      },
      () => {
        if (!cancelled) {
          setBlobUrl(null);
        }
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user.avatar_url, tokens?.access_token, absoluteHttp]);

  const src = absoluteHttp ?? blobUrl;

  if (src) {
    return (
      <span className={wrapperClass} style={style}>
        <img src={src} alt={user.full_name ?? 'avatar'} />
      </span>
    );
  }

  return (
    <span className={wrapperClass} style={style} aria-hidden={!user.full_name}>
      {computeInitials(user.full_name)}
    </span>
  );
}
